import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { IconButton, Modal, Tooltip } from "@mui/material";
import { Close, Info } from "@mui/icons-material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HourglassBottomIcon from "@mui/icons-material/HourglassBottom";
import DoneIcon from "@mui/icons-material/Done";
import CloseIcon from "@mui/icons-material/Close";

import Styles from "./Settlement.module.css";
import { ErrorMessage } from "../../../../commons/Toast/ErrorMessage";
import ConfirmationModal from "../../../../commons/ConfirmationModal/ConfirmationModal";
import {
  finalSettlementMakerChecker,
  oneTimeSettlement,
  schedulePayouts,
  updateRepScheduleStatus,
  userFinalSettlement,
  userFinalSettlementGet,
  viewSettlement,
} from "../../../../Services/Schedule/schedule";
import Payouts from "../Payouts/Payouts";
import { formatSlotTime } from "../../../../helpers/dateFormat";
import { useConnectionData } from "../../../ConsumerGenerator/ListingContainer/AvailableListing/AvailableListing";
import { walletAccountApi } from "../../../../Services/Payments/PaymentApi";
import { convertDate } from "../../../../commons/FormatDate";

// --- CONSTANTS ---
const COLUMN_WIDTHS = {
  sm: "5%",
  md: "10%",
  lg: "20%",
};

// --- SUB-COMPONENT: HEADER ---
const SettlementTableHeader = ({ role }) => (
  <div className={`tableHeading display_flex_row`}>
    <div style={{ width: "10%" }}>From-To</div>
    {/* <div style={{ width: "10%" }}>To</div> */}
    <div style={{ width: "20%" }}>Charge Type</div>
    <div style={{ width: "10%" }}>Charge Rate</div>
    <div style={{ width: "10%" }}>
      Est. Amount (₹){" "}
      <Tooltip title={"Estimated amount as per schedule quantum."} arrow>
        <Info
          style={{
            fontSize: 16,
            color: "#666",
            marginLeft: "4px",
          }}
        />
      </Tooltip>
    </div>
    <div style={{ width: "5%" }}>Maker</div>
    <div style={{ width: "5%" }}>Checker</div>
    <div style={{ width: "10%" }}>
      Final Amount (₹){" "}
      <Tooltip title={"Final amount as per delivered quantum."} arrow>
        <Info
          style={{
            fontSize: 16,
            color: "#666",
            marginLeft: "4px",
          }}
        />
      </Tooltip>
    </div>
    <div style={{ width: "10%" }}>Maker Comment</div>
    <div style={{ width: "10%" }}>Checker Comment</div>
    {(role === "CHECKER" || role === "MAKER") && (
      <div style={{ width: "5%" }}>Verify</div>
    )}
  </div>
);

// --- SUB-COMPONENT: ROW ---
const SettlementRow = ({
  ele,
  index,
  role,
  onAmountChange,
  onCheckerAction,
  rawDamValue,
  type,
}) => {
  console.log("🚀 ~ SettlementRow ~ type:", type);
  const isAccepted = (val) =>
    val !== null && (ele?.status === "CHECKER" || ele?.status === "PAYMENT");
  const isCheckerAccepted = (val) => val !== null && ele?.status === "PAYMENT";

  const renderStatusIcon = (accepted) =>
    accepted ? (
      <CheckCircleIcon style={{ fontSize: "14px" }} />
    ) : (
      <HourglassBottomIcon style={{ fontSize: "14px" }} />
    );

  // Determine if input is read-only based on Role and Status
  const isReadOnly = (() => {
    if (role === "MAKER" && ["CHECKER", "PAYMENT"].includes(ele?.status))
      return true;
    if (role === "CHECKER") return true; // Checker generally only verifies
    if (
      (role === "CONSUMER" ||
        role === "GENERATOR" ||
        role === "SUPER_ADMIN" ||
        role === "ADMIN") &&
      ["MAKER", "CHECKER", "PAYMENT"].includes(ele?.status)
    )
      return true;
    return false;
  })();

  // Determine which value to display/edit
  const getInputValue = () => {
    if (role === "MAKER") return ele?.makerInputChargeAmount;

    if (role === "CHECKER") {
      // Returns the first value that is NOT null or undefined
      return (
        ele?.checkerInputChargeAmount ??
        ele?.makerInputChargeAmount ??
        ele?.userInputChargeAmount
      );
    }

    return ele?.userInputChargeAmount ?? ele?.amount;
  };

  return (
    <div className="display_flex_row tableDetails">
      <div style={{ width: "10%" }}>
        {ele?.fromUser || "NA"} - {ele?.toUser || "NA"}
      </div>
      {/* <div style={{ width: "10%" }}></div> */}
      <div
        title={ele?.name || ele?.chargeName || "-"}
        style={{
          width: "20%",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {ele?.name || ele?.chargeName || "-"}
      </div>
      <div style={{ width: "10%" }}>
        {(ele?.type || ele?.chargeType) === "PER_MW"
          ? `₹${ele?.value || ele?.chargeValue?.toFixed(3) || 0}/kWh`
          : "Fixed"}{" "}
        <br />
        {ele?.fromUser === "Consumer" && ele?.toUser === "Generator" && (
          <span style={{ fontSize: "8px", color: "#f1935c" }}>
            {rawDamValue}
          </span>
        )}
      </div>
      <div style={{ width: "10%" }}>
        {ele?.amount || ele?.chargeAmount?.toFixed(3) || "-"}
      </div>
      <div
        style={{ width: "5%" }}
        className={
          (role === "MAKER" &&
            (ele?.status === "CHECKER" || ele?.status === "PAYMENT")) ||
          role === "CHECKER" ||
          ((role === "CONSUMER" || role === "GENERATOR") &&
            (ele?.status === "MAKER" ||
              ele?.status === "CHECKER" ||
              ele?.status === "PAYMENT"))
            ? "accepted"
            : "pending"
        }
      >
        {renderStatusIcon(
          (role === "MAKER" &&
            (ele?.status === "CHECKER" || ele?.status === "PAYMENT")) ||
            role === "CHECKER" ||
            ((role === "CONSUMER" || role === "GENERATOR") &&
              (ele?.status === "MAKER" ||
                ele?.status === "CHECKER" ||
                ele?.status === "PAYMENT")),
        )}
      </div>
      <div
        style={{ width: "5%" }}
        className={
          isCheckerAccepted(ele?.checkerInputChargeAmount) ||
          ele?.status === "PAYMENT"
            ? "accepted"
            : "pending"
        }
      >
        {renderStatusIcon(
          isCheckerAccepted(ele?.checkerInputChargeAmount) ||
            ele?.status === "PAYMENT",
        )}
      </div>
      <div style={{ width: "10%" }} className={Styles.finalAmountInput}>
        {isReadOnly || (ele?.isNonEditable && role === "CONSUMER") ? (
          <>{getInputValue()}</>
        ) : (
          <input
            type="text"
            value={getInputValue() || ""}
            onChange={(e) => onAmountChange(e, index)}
          />
        )}
      </div>

      <div style={{ width: "10%" }}>
        {" "}
        {ele?.makerComment ? (
          <Tooltip title={ele?.makerComment} arrow>
            <IconButton
              size="small"
              sx={{
                maxWidth: "100%",
                padding: "4px",
                "& .MuiIconButton-label": {
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  display: "block",
                  fontSize: "12px",
                },
              }}
            >
              <span
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  display: "block",
                  fontSize: "12px",
                  maxWidth: "100%",
                }}
              >
                {ele?.makerComment}
              </span>
            </IconButton>
          </Tooltip>
        ) : (
          "-"
        )}
      </div>

      <div style={{ width: "10%" }}>
        {" "}
        {ele?.checkerComment ? (
          <Tooltip title={ele?.checkerComment} arrow>
            <IconButton
              size="small"
              sx={{
                maxWidth: "100%",
                padding: "4px",
                "& .MuiIconButton-label": {
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  display: "block",
                  fontSize: "12px",
                },
              }}
            >
              <span
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  display: "block",
                  fontSize: "12px",
                  maxWidth: "100%",
                }}
              >
                {ele?.checkerComment}
              </span>
            </IconButton>
          </Tooltip>
        ) : (
          "-"
        )}
      </div>

      {(role === "CHECKER" || role === "MAKER") && (
        <div
          style={{
            display: "flex",
            width: "5%",
            alignItems: "center",
            justifyContent: "center",
            gap: "4px",
          }}
        >
          {role === "CHECKER" && type !== "HISTORY" && (
            <>
              {ele?.status === "RECHECK" ? (
                <div className={Styles.verifyIconsBlock}>
                  <CloseIcon style={{ fontSize: "14px" }} />
                </div>
              ) : (
                <div className={Styles.closeIcon}>
                  <CloseIcon
                    style={{ fontSize: "14px" }}
                    onClick={() => onCheckerAction("reject", ele, index)}
                  />
                </div>
              )}
            </>
          )}

          {(role === "CHECKER" &&
            ele?.status !== "PAYMENT" &&
            type !== "HISTORY") ||
          (role === "MAKER" &&
            ele?.status === "MAKER" &&
            type !== "HISTORY") ? (
            <div className={Styles.verifyIcons}>
              <DoneIcon
                style={{ fontSize: "14px" }}
                onClick={() => onCheckerAction("accept", ele, index)}
              />
            </div>
          ) : (
            <div className={Styles.verifyIconsBlock}>
              <DoneIcon style={{ fontSize: "14px" }} />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// --- SUB-COMPONENT: FOOTER BUTTONS ---
const SettlementFooter = ({ role, status, settlementDetails, onAction }) => {
  const isReadOnlyConsumer =
    (role === "CONSUMER" || role === "GENERATOR") &&
    ["MAKER", "CHECKER", "PAYMENT"].includes(status);
  const isReadOnlyMaker =
    role === "MAKER" && ["CHECKER", "PAYMENT"].includes(status);
  const isReadOnlyChecker = role === "CHECKER" && status === "PAYMENT";

  if (isReadOnlyConsumer || isReadOnlyMaker || isReadOnlyChecker) return null;

  return (
    <div className={`${Styles.settlementModals}`}>
      {role === "CONSUMER" && settlementDetails?.length > 0 && (
        <div
          className={`activeButton ${Styles.continueButton}`}
          onClick={() => onAction("submit")}
        >
          {role === "CONSUMER" ? "Submit" : "SUBMIT"}
        </div>
      )}

      {role === "MAKER" &&
        settlementDetails?.every((ele) => ele?.status === "CHECKER") && (
          <div
            className={`activeButton ${Styles.continueButton}`}
            onClick={() => onAction("submit")}
          >
            {role === "CONSUMER" ? "Submit" : "SUBMIT"}
          </div>
        )}

      {role === "CHECKER" && (
        <>
          <div
            className={`delete_button ${Styles.continueButton}`}
            onClick={() => onAction("rejectAll")}
          >
            Reject
          </div>
          {settlementDetails?.some((ele) => ele?.status === "RECHECK") && (
            <div
              className={`activeButton ${Styles.continueButton}`}
              onClick={() => onAction("transferToMaker")}
            >
              Transfer to Maker
            </div>
          )}
          {settlementDetails?.every((ele) => ele?.status === "PAYMENT") && (
            <div
              className={`activeButton ${Styles.continueButton}`}
              onClick={() => onAction("payout")}
            >
              SUBMIT
            </div>
          )}
        </>
      )}
    </div>
  );
};

// --- MAIN COMPONENT ---
const Settlement = ({
  open,
  handleClose,
  id,
  data,
  handleDeliveryStatus,
  admin,
  renderScheuleData,
  isLast,
  selectedScheduleId,
  paymentScreen = false,
  paymentData,
  heading,
  connection,
  type,
}) => {
  const role = useSelector(
    (state) => state?.persistedReducer?.user?.activeRole,
  );
  const userId = useSelector(
    (state) => state?.persistedReducer?.user?.user?.userId,
  );
  const selfCompanyName = useSelector(
    (state) => state?.persistedReducer?.user?.company?.displayName,
  );

  const [settlementDetails, setSettlementDetails] = useState([]);
  const [activeRowData, setActiveRowData] = useState(null);
  const [payoutOpen, setPayoutOpen] = useState(false);
  const [confirmModal, setConfirmModal] = useState({
    status: false,
    data: "",
    note: "",
    type: null,
  });

  const rawDamValue = (() => {
    const price = data?.calculatedDamPrice || 0;
    const rate = data?.energyRate || 0;

    // If your business logic requires adding the rate back for DAM_MINUS:
    if (data?.energyRateType === "DAM_MINUS") {
      return `DAM-${rate}(DAM=${(price + rate).toFixed(3)})`;
    }
    if (data?.energyRateType === "DAM_PLUS") {
      return `DAM+${rate}(DAM=${(price - rate).toFixed(3)})`;
    }
    return null;
  })();

  // --- 1. Initial Data Fetch ---
  useEffect(() => {
    const fetchData = async () => {
      if (paymentScreen && paymentData?.contractId) {
        await handleOneTimeSettlement();
      } else if (
        paymentScreen ||
        admin ||
        ["MAKER", "CHECKER", "PAYMENT"].includes(data?.status)
      ) {
        await handleAdminGetSettlement();
      } else {
        await handleGetSettlement();
      }
    };
    fetchData();
  }, [id, data, admin, role]);

  const handleGetSettlement = async () => {
    let res = await viewSettlement({
      id: id,
      connectionId: data?.bilateralConnectionId,
      isForOneDay: true,
      repScheduleId: data?.id,
    });
    if (res?.data?.code === 200) {
      if (role === "CONSUMER") {
        const processedData = processConsumerViewData(
          res?.data?.data,
          data,
        ).filter(
          (ele) =>
            ele?.frequency !== "ONE_TIME" ||
            ele?.name === "Extra Margin" ||
            ele?.name === "Energy Charge" ||
            ele?.name === "Refund Request",
        );
        setSettlementDetails(processedData);
      }
    } else {
      ErrorMessage(res?.data?.error);
      handleClose();
    }
  };

  const normalizeAmount = (value) => {
    if (value === null || value === undefined || value === "") return "";
    const num = Number(value);
    return Number.isNaN(num) ? String(value).trim() : num.toFixed(3);
  };

  const handleAdminGetSettlement = async () => {
    let res = await userFinalSettlementGet({
      scheduleId: data?.scheduleId || paymentData?.schId,
      repScheduleId: selectedScheduleId || data?.id || paymentData?.repId,
    });
    if (res?.data?.code === 200) {
      const processed = processAdminViewData(res?.data?.data, role).map(
        (item) => ({
          ...item,
          _initialMakerInputChargeAmount:
            item?.makerInputChargeAmount ?? item?.userInputChargeAmount ?? "",
          isMakerAmountEdited: false,
        }),
      );
      setSettlementDetails(processed);
    }
  };

  const handleOneTimeSettlement = async () => {
    let res = await oneTimeSettlement({
      bCId: data?.bilateralContractId || paymentData?.contractId,
    });
    if (res?.data?.code === 200) {
      const processed = processAdminViewData(res?.data?.data, role).map(
        (item) => ({
          ...item,
          _initialMakerInputChargeAmount:
            item?.makerInputChargeAmount ?? item?.userInputChargeAmount ?? "",
          isMakerAmountEdited: false,
        }),
      );
      setSettlementDetails(processed);
    }
  };

  // --- 2. Input Handlers ---
  const handleAmountChange = (e, index) => {
    const val = e.target.value;
    if (!/^\d*\.?\d{0,3}$/.test(val)) return;

    setSettlementDetails((prev) =>
      prev.map((ele, idx) => {
        if (idx !== index) return ele;

        if (role === "MAKER") {
          const initialVal = normalizeAmount(
            ele?._initialMakerInputChargeAmount,
          );
          const currentVal = normalizeAmount(val);
          const isInitialEmpty = initialVal === "" || initialVal === "0.000";
          const edited =
            initialVal !== currentVal || (isInitialEmpty && val !== "");

          return {
            ...ele,
            makerInputChargeAmount: val,
            isMakerAmountEdited: edited,
          };
        }

        if (role === "CHECKER") {
          return { ...ele, checkerInputChargeAmount: val };
        }

        return { ...ele, userInputChargeAmount: val };
      }),
    );
  };

  const handleCommentChange = (comment) => {
    setSettlementDetails((prev) =>
      prev.map((ele, idx) => {
        if (idx !== activeRowData?.index) return ele;

        if (role === "MAKER") {
          return { ...ele, makerComment: comment, comment };
        }

        if (role === "CHECKER") {
          return { ...ele, checkerComment: comment, comment };
        }

        return { ...ele, comment };
      }),
    );
  };
  // --- 3. Action Handlers (Logic) ---

  // Handles row-level Verify/Reject clicks (Checker)
  const handleCheckerAction = (type, rowData, index) => {
    setActiveRowData({ ...rowData, index });
    if (type === "reject") {
      setConfirmModal({
        status: true,
        type: "reject_row",
        data: "Do you want to reject this value?",
        note: "Once rejected, Maker will need to update the values again.",
      });
    } else if (type === "accept") {
      if (role === "CHECKER") {
        setConfirmModal({
          status: true,
          type: "accept_row",
          data: "Do you want to accept this value?",
          note: "Once accepted, payment will be processed as per the final values and transferred through Bank NOCA.",
        });
      } else {
        setConfirmModal({
          status: true,
          type: "accept_row",
          data: "Do you want to accept this value?",
          note: "Once transferred, Checker will verify the amount.",
        });
      }
    }
  };

  // Handles footer button clicks
  const handleFooterAction = (actionType) => {
    if (actionType === "submit") {
      setConfirmModal({
        status: true,
        type: "submit_all",
        data: "Do you want to transfer to maker-checker?",
        note: "After confirmation, the payment will be forwarded to the checker for approval. Upon approval, the amount will be transferred through Bank NOCA.",
      });
    } else if (actionType === "rejectAll") {
      // handleRejectAll();
      setConfirmModal({
        status: true,
        type: "reject_all",
        data: "Do you want to reject the transaction?",
        note: "It will be transferred back to the maker for verification.",
      });
    } else if (actionType === "transferToMaker") {
      setConfirmModal({
        status: true,
        type: "transfer_maker",
        data: "Do you want to reject this transaction?",
        note: "It will be transferred back to the maker.",
      });
    } else if (actionType === "payout") {
      setPayoutOpen(true);
    }
  };

  // --- 4. Submission Logic ---
  const getWalletId = async ({ role, id }) => {
    const res = await walletAccountApi({ role, id });
    return res?.data?.code === 200
      ? (res?.data?.data?.walletAccounts?.[0]?.id ?? null)
      : null;
  };
  // Main Submit Function (Used for Maker submit, Consumer submit, Checker Transfer)
  const submitSettlementData = async ({ isReject = false } = {}) => {
    // 1. Update status
    let nextStatus =
      role === "MAKER" ? "CHECKER" : role === "CHECKER" ? "PAYMENT" : "MAKER";
    if (isReject) nextStatus = "MAKER";

    const statusRes = await updateRepScheduleStatus({
      id: data?.id,
      status: nextStatus,
    });
    if (statusRes?.data?.code !== 200) {
      return ErrorMessage(statusRes?.data?.error);
    }

    const [consumerWalletId, generatorWalletId] = await Promise.all([
      getWalletId({
        role: "CONSUMER",
        id: connection?.capacityConsumer?.consumerId,
      }),
      getWalletId({
        role: "GENERATOR",
        id: connection?.capacityGenerator?.generatorId,
      }),
    ]);

    // 2. Prepare Payload
    const payload = prepareSubmissionPayload({
      details: settlementDetails,
      role,
      userId,
      scheduleId: data?.scheduleId,
      isReject,
      data,
      selectedScheduleId,
      consumerWalletId,
      generatorWalletId,
    });

    // 3. API Call
    let res;
    if (role === "MAKER" || role === "CHECKER") {
      res = await finalSettlementMakerChecker(payload);
    } else {
      res = await userFinalSettlement(payload);
    }

    if (res?.data?.code === 200) {
      if (role === "CONSUMER") {
        handleDeliveryStatus({
          id: data?.id,
          scheduleId: data?.scheduleId,
          settlement: true,
          payment: true,
        });
      } else {
        renderScheuleData();
        handleAdminGetSettlement();
      }
      setConfirmModal({ status: false });
      handleClose();
    } else {
      ErrorMessage(res?.data?.error);
    }
  };

  const handleRejectAll = async () => {
    await submitSettlementData({ isReject: true });
  };

  const handlePayoutsSubmit = async ({ list }) => {
    const payload = list?.map((ele) => ({
      bilateralContractId: data?.bilateralContractId,
      fromUserId: ele.fromUserId,
      toUserId: ele.toUserId,
      transferAmount: ele.totalAmount,
      receiverType:
        ele?.toUser === ele?.fromUser
          ? "REFUND"
          : ele?.toUser === "Consumer"
            ? "CONSUMER"
            : ele?.toUser === "Generator"
              ? "GENERATOR"
              : "OTHERS",
      fromUser: ele?.fromUser,
      toUser: ele?.toUser,
      status: "PENDING",
      transactionType: ele?.transactionType,
      repScheduleId: ele?.repScheduleId,
      bearer: ele?.bearer,
      payer: ele?.payer,
      creditWalletAccountId: ele?.creditWalletAccountId,
      debitWalletAccountId: ele?.debitWalletAccountId,
    }));
    let res = await schedulePayouts(payload);
    if (res?.data?.code === 200) {
      await submitSettlementData();
    }
  };

  // Handle local state updates for Checker (Verify/Reject Row)
  const handleLocalRowUpdate = (type) => {
    setSettlementDetails((prev) =>
      prev.map((ele, idx) => {
        // Compare index instead of ID
        if (idx !== activeRowData?.index) return ele;

        if (type === "accept_row") {
          return {
            ...ele,
            status: role === "MAKER" ? "CHECKER" : "PAYMENT",
            checkerInputChargeAmount:
              role === "CHECKER"
                ? ele.checkerInputChargeAmount || ele.makerInputChargeAmount
                : null,
            makerInputChargeAmount:
              role === "CHECKER"
                ? ele.checkerInputChargeAmount || ele.makerInputChargeAmount
                : ele.makerInputChargeAmount,
          };
        }
        if (type === "reject_row") {
          return {
            ...ele,
            status: "RECHECK",
            checkerInputChargeAmount: null,
            makerInputChargeAmount: null,
          };
        }
        return ele;
      }),
    );
    setConfirmModal({ status: false, data: "", note: "", type: null });
  };

  // Master Modal Confirm Handler
  const handleModalSubmit = () => {
    const { type } = confirmModal;

    if (type === "accept_row" && role === "MAKER") {
      const activeRow = settlementDetails?.[activeRowData?.index];
      const isAmountChanged = activeRow?.isMakerAmountEdited === true;
      const hasComment = activeRow?.makerComment?.trim();

      if (isAmountChanged && !hasComment) {
        ErrorMessage("Maker comment is required when Final Amount is changed.");
        return;
      }
    }

    if (type === "reject_row" || type === "accept_row")
      return handleLocalRowUpdate(type);
    if (type === "transfer_maker")
      return submitSettlementData({ isReject: true });
    return submitSettlementData();
  };

  return (
    <>
      {payoutOpen && (
        <Payouts
          open={payoutOpen}
          handleClose={() => setPayoutOpen(false)}
          list={settlementDetails}
          handlePayoutsSubmit={handlePayoutsSubmit}
        />
      )}

      {confirmModal.status && (
        <ConfirmationModal
          open={confirmModal.status}
          data={confirmModal.data}
          note={confirmModal.note || data?.note}
          header={"Confirmation"}
          handleSubmit={handleModalSubmit}
          handleClose={() => setConfirmModal({ status: false })}
          handleCommentChange={handleCommentChange}
          commentBox={
            confirmModal.type === "reject_row" ||
            confirmModal.type === "accept_row"
          }
          commentInit={
            confirmModal.type === "reject_row" ||
            (confirmModal.type === "accept_row" &&
              role === "MAKER" &&
              settlementDetails?.[activeRowData?.index]?.isMakerAmountEdited)
          }
        />
      )}

      <Modal open={open} onClose={handleClose}>
        <div className={`popupModal ${Styles.settlementMainModal}`}>
          <div
            className={`display_flex_row modalHeading ${Styles.modalHeading}`}
          >
            <div className="headingText">
              {heading || "Transaction Details"}
            </div>
            <Close
              style={{ fontSize: 24, color: "#777777", cursor: "pointer" }}
              onClick={handleClose}
            />
          </div>
          {!paymentScreen && (
            <div className={Styles.tranScheduleContainer}>
              <SummaryItem
                label="Contract ID"
                value={data?.bilateralContractUniqueId || "NA"}
              />
              <SummaryItem
                label="Buyer"
                value={data?.selfCompanyName || selfCompanyName}
              />
              <SummaryItem
                label="Seller"
                value={data?.counterPartyCompanyName}
              />
              {data?.fromDate && (
                <SummaryItem
                  label="Delivery Date"
                  value={convertDate(data?.fromDate) || "-"}
                />
              )}
              <SummaryItem
                label="Time Range"
                value={
                  data?.type === "ONE_TIME"
                    ? "-"
                    : `${data?.fromBlock?.timeBlock?.split("-")[0]} to ${
                        data?.toBlock?.timeBlock?.split("-")[1]?.trim() ===
                        "23:59:59"
                          ? "24:00"
                          : formatSlotTime(
                              data?.toBlock?.timeBlock?.split("-")?.[1]?.trim(),
                            )
                      }`
                }
              />
              <SummaryItem
                label="Scheduled Volume"
                value={
                  data?.type === "ONE_TIME"
                    ? "-"
                    : `${data?.scheduleVolume?.toFixed(2)}MWh`
                }
              />
              <SummaryItem
                label="Delivered Volume"
                value={
                  data?.type === "ONE_TIME"
                    ? "-"
                    : `${data?.deliveredVolume?.toFixed(2)}MWh`
                }
              />
              {/* <SummaryItem
              label="Energy Rate"
              value={`${data?.energyRate}₹/kWh`}
            />
            <div className={Styles.selectionDetails}>
              <div>Status</div>
              <div className="pending">{data?.status}</div>
            </div> */}
            </div>
          )}

          <div className={Styles.settlementDetails}>
            <SettlementTableHeader role={role} />
            <div
              className={`scroll_bar_y ${Styles.settlementDetailsContainer}`}
            >
              {settlementDetails
                ?.map((ele, originalIndex) => ({ ele, originalIndex }))
                .filter(({ ele }) => {
                  if (!ele?.isView) return false;
                  const name = ele?.name || ele?.chargeName;
                  const isTraderOrPlatform =
                    name === "Trader Margin" || name === "Platform Fees";
                  if (
                    role === "CONSUMER" &&
                    ele?.fromUser === "Generator" &&
                    isTraderOrPlatform
                  )
                    return false;
                  if (
                    role === "GENERATOR" &&
                    ele?.fromUser === "Consumer" &&
                    isTraderOrPlatform
                  )
                    return false;
                  return true;
                })
                .sort((a, b) => {
                  const aIsGna = (a.ele?.name || a.ele?.chargeName)?.endsWith(
                    "_GNA",
                  );
                  const bIsGna = (b.ele?.name || b.ele?.chargeName)?.endsWith(
                    "_GNA",
                  );
                  if (aIsGna === bIsGna) return 0;
                  return aIsGna ? -1 : 1;
                })
                .map(({ ele, originalIndex }) => (
                  <SettlementRow
                    key={originalIndex}
                    index={originalIndex}
                    ele={ele}
                    role={role}
                    onAmountChange={handleAmountChange}
                    onCheckerAction={handleCheckerAction}
                    rawDamValue={rawDamValue}
                    type={type}
                  />
                ))}
            </div>
          </div>

          {!paymentScreen && (
            <SettlementFooter
              role={role}
              status={data?.status}
              settlementDetails={settlementDetails}
              onAction={handleFooterAction}
            />
          )}
        </div>
      </Modal>
    </>
  );
};

// --- HELPER COMPONENTS ---
const SummaryItem = ({ label, value }) => (
  <div className={Styles.selectionDetails}>
    <div>{label}</div>
    <div className={Styles.labelValue}>{value}</div>
  </div>
);

export default Settlement;

// --- UTILITY FUNCTIONS (PURE LOGIC) ---

const processConsumerViewData = (responseData, scheduleData) => {
  const common = {
    scheduleId: scheduleData?.scheduleId,
    makerId: null,
    checkerId: null,
    actualChargeAmount: null,
    makerInputChargeAmount: null,
    checkerInputChargeAmount: null,
    isView: true,
  };

  const createCharge = (base, overrides) => ({
    ...base,
    ...common,
    ...overrides,
  });

  // 1. Calculate General Details (Consumer -> Generator)
  const genDetails = createCharge({
    fromUser: "Consumer",
    toUser: "Generator",
    name: "Energy Charge",
    frequency: "RECURRING",
    type: "PER_MW",
    value:
      scheduleData?.calculatedDamPrice?.toFixed(3) || scheduleData?.energyRate,
    isAdminCharge: false,
    amount: responseData?.totalAmountToGenerator?.toFixed(3),
    userInputChargeAmount: (
      (responseData?.totalAmountToGenerator * scheduleData?.deliveredVolume) /
      scheduleData?.scheduleVolume
    )?.toFixed(3),
  });

  // 2. Calculate Consumer Charges Breakdown
  const processedCharges = (responseData?.consumerChargesBreakDown || []).map(
    (charge) =>
      createCharge(charge, {
        fromUser: "Consumer",
        toUser: charge?.chargeType,
        isNonEditable: charge?.isAdminCharge,
        userInputChargeAmount:
          charge?.type === "FIXED"
            ? charge?.amount
            : (
                (charge?.amount * scheduleData?.deliveredVolume) /
                scheduleData?.scheduleVolume
              )?.toFixed(3),
      }),
  );

  // 3. Calculate Generator Charges Breakdown
  const processedChargesGen = (
    responseData?.generatorChargesBreakDown || []
  ).map((charge) =>
    createCharge(charge, {
      fromUser: "Generator",
      toUser: charge?.chargeType,
      isNonEditable: charge?.isAdminCharge,
      userInputChargeAmount:
        charge?.type === "FIXED"
          ? charge?.amount
          : (
              (charge?.amount * scheduleData?.deliveredVolume) /
              scheduleData?.scheduleVolume
            )?.toFixed(3),
      isView: false,
    }),
  );

  /**
   * Helper to calculate the difference (Refund amount)
   * Formula: Original Amount - User Input (Pro-rated) Amount
   */
  const calculateRefund = (items) => {
    return items.reduce((acc, item) => {
      const original = item.amount || 0;
      const calculated = item.userInputChargeAmount || 0;
      return acc + (original - calculated);
    }, 0);
  };

  // Calculate specific refund totals
  const consumerRefundTotal = calculateRefund([
    genDetails,
    ...processedCharges,
  ]);
  const generatorRefundTotal = calculateRefund(processedChargesGen);

  // 4. Create Consumer Refund Object
  const refund = createCharge({
    fromUser: "Consumer",
    toUser: "Consumer",
    name: "Refund Request",
    frequency: "RECURRING",
    type: "FIXED",
    // Set the calculated difference as the amount
    amount: 0,
    userInputChargeAmount: consumerRefundTotal?.toFixed(3),
    isNonEditable: true,
  });

  // 5. Create Generator Refund Object
  const refundGen = createCharge(refund, {
    fromUser: "Generator",
    toUser: "Generator",
    name: "Refund Request",
    isView: false,
    // Set the calculated difference as the amount
    amount: generatorRefundTotal?.toFixed(3),
    userInputChargeAmount: generatorRefundTotal?.toFixed(3),
    isNonEditable: true,
  });

  const refundConMargin = createCharge({
    fromUser: "Consumer",
    toUser: "Consumer",
    name: "Extra Margin",
    frequency: "RECURRING",
    type: "FIXED",
    // Set the calculated difference as the amount
    amount: responseData?.consumerRefundAmount?.toFixed(3) || 0,
    userInputChargeAmount: responseData?.consumerRefundAmount?.toFixed(3) || 0,
    isNonEditable: true,
  });

  // 5. Create Generator Refund Object
  const refundGenMargin = createCharge(refund, {
    fromUser: "Generator",
    toUser: "Generator",
    name: "Extra Margin",
    isView: false,
    // Set the calculated difference as the amount
    amount: responseData?.generatorRefundAmount?.toFixed(3) || 0,
    userInputChargeAmount: responseData?.generatorRefundAmount?.toFixed(3) || 0,
    isNonEditable: true,
  });

  return [
    genDetails,
    ...processedCharges,
    refundConMargin,
    refund,
    ...processedChargesGen,
    refundGen,
    refundGenMargin,
  ];
};

const processAdminViewData = (charges, role) => {
  if (!charges) return [];
  return charges.map((charge) => {
    let updatedCharge = { ...charge, isView: true };
    if (role === "MAKER") {
      updatedCharge.makerInputChargeAmount = charge?.userInputChargeAmount || 0;
    } else if (role === "CHECKER") {
      updatedCharge.checkerInputChargeAmount =
        charge?.makerInputChargeAmount || 0;
    }
    return updatedCharge;
  });
};

const prepareSubmissionPayload = ({
  details,
  role,
  userId,
  scheduleId,
  isReject = false,
  data,
  selectedScheduleId,
  consumerWalletId,
  generatorWalletId,
}) => {
  return details.map((ele) => {
    const isMaker = role === "MAKER";
    const isChecker = role === "CHECKER";

    // Status Determination
    let status = "MAKER";
    if (isReject) status = "MAKER";
    else if (isMaker) status = "CHECKER";
    else if (isChecker) status = "PAYMENT";

    // ID Logic
    const selfId = data?.selfPartyEntityId;
    const counterId = data?.counterPartyEntityId;

    // Determine From/To IDs
    let fromUserId = ele?.fromUser === "Consumer" ? selfId : counterId;
    let toUserId;

    if (ele?.fromUser === "Consumer" && ele?.toUser === "Generator") {
      toUserId = ele?.fromUser === "Consumer" ? counterId : selfId;
    } else if (ele?.fromUser === ele?.toUser) {
      // Refund scenarios (Consumer->Consumer or Generator->Generator)
      toUserId = ele?.fromUser === "Consumer" ? selfId : counterId;
    } else {
      // Transfer (Consumer->Generator or Vice Versa)
      toUserId = ele?.toEntityId;
    }

    return {
      id: isMaker || isChecker ? ele?.id : null,
      fromUser: ele?.fromUser,
      fromUserId: ele?.fromUserId || fromUserId,
      toUser: ele?.toUser,
      toUserId: ele?.toUserId || ele?.toEntityId || toUserId,
      chargeName: ele?.name || ele?.chargeName,
      chargeFrequency: ele?.frequency || ele?.chargeFrequency,
      chargeType: ele?.type || ele?.chargeType,
      isAdminCharge: ele?.isAdminCharge || false,
      chargeAmount:
        ele?.amount || ele?.chargeAmount || ele?.userInputChargeAmount || 0,
      scheduleId: scheduleId,
      chargeValue: ele?.value || ele?.chargeValue || 0,
      makerId: isMaker || ele?.makerId ? ele?.makerId || userId : null,
      checkerId: isChecker || ele?.checkerId ? ele?.checkerId || userId : null,
      actualChargeAmount: ele?.actualChargeAmount || ele?.amount,
      userInputChargeAmount: ele?.userInputChargeAmount || 0,
      makerInputChargeAmount: ele?.makerInputChargeAmount,
      checkerInputChargeAmount: isMaker ? 0 : ele?.checkerInputChargeAmount,
      status: status,
      repScheduleId: selectedScheduleId || ele?.repScheduleId,
      makerComment: ele?.makerComment,
      checkerComment: ele?.checkerComment,
      bearer:
        ele?.fromUser === "Consumer"
          ? "BUYER"
          : ele?.fromUser === "Generator"
            ? "SELLER"
            : "APPLICANT",

      payer:
        ele?.toUser === "Consumer"
          ? "BUYER"
          : ele?.toUser === "Generator"
            ? "SELLER"
            : "APPLICANT",
      creditWalletAccountId:
        ele?.creditWalletAccountId ||
        (ele?.toUser === "Consumer"
          ? consumerWalletId
          : ele?.toUser === "Generator"
            ? generatorWalletId
            : null),
      debitWalletAccountId:
        ele?.debitWalletAccountId ||
        (ele?.fromUser === "Consumer"
          ? consumerWalletId
          : ele?.fromUser === "Generator"
            ? generatorWalletId
            : null),
    };
  });
};
